import type { DemoState, FlowStep } from "./model";
const steps: FlowStep[] = [
  { id: "start", type: "entry", text: "Call arrives", x: 80, y: 60 },
  {
    id: "greet",
    type: "say",
    text: "You have reached Blue Ridge Fiber. I am the automated assistant.",
    x: 80,
    y: 190,
  },
  {
    id: "check",
    type: "lookup",
    text: "Check for a known outage",
    x: 80,
    y: 320,
  },
  {
    id: "message",
    type: "message",
    text: "Take a voicemail message",
    x: 80,
    y: 450,
  },
  {
    id: "page",
    type: "page",
    text: "Page the on-call technician for urgent messages",
    x: 80,
    y: 580,
  },
  { id: "end", type: "end", text: "End call", x: 80, y: 710 },
];
const edges = steps
  .slice(1)
  .map((s, i) => ({ id: `edge-${i}`, source: steps[i]!.id, target: s.id }));
export function createSeed(): DemoState {
  return {
    stormArmed: false,
    additionalOutages: [],
    decisionRules: {},
    numbers: [
      {
        id: "number-1",
        e164: "+13045550100",
        label: "Main support line",
        active: true,
      },
    ],
    outage: {
      status: "confirmed",
      label: "Sugar Grove tower",
      scope: "Node SG-2",
      affectedCount: 63,
      bannerText:
        "We're aware of an outage in Sugar Grove. Crews are working on it. We don't have a restoration time yet.",
      etaText: "",
      etaSource: "",
      waiting: 15,
    },
    calls: [
      {
        id: "call-1",
        time: "2:08 AM",
        from: "(304) 555-0185",
        name: "Evelyn Harper",
        account: "BR-13012",
        reason: "Outage information",
        outcome: "Informed",
        seconds: 47,
        flagged: false,
        recordingUrl: null,
        transcript: [
          {
            who: "assistant",
            text: "Thank you for calling Blue Ridge Fiber. I am an automated assistant. How can I help?",
          },
          {
            who: "caller",
            text: "Our internet is down in Sugar Grove. Is there an outage?",
          },
          {
            who: "assistant",
            text: "We're aware of an outage in Sugar Grove. Crews are working on it. We don't have a restoration time yet.",
          },
          { who: "caller", text: "Thank you. That is all I needed." },
        ],
      },
      {
        id: "call-2",
        time: "1:57 AM",
        from: "(304) 555-0135",
        name: "Thomas Reed",
        account: "BR-12998",
        reason: "Intermittent connection",
        outcome: "Message taken",
        seconds: 72,
        flagged: false,
        recordingUrl: null,
        transcript: [
          {
            who: "caller",
            text: "Our connection has been dropping since dinner.",
          },
          {
            who: "assistant",
            text: "I can take a message for the team to review in the morning. What is the best callback number?",
          },
        ],
      },
      {
        id: "call-3",
        time: "1:46 AM",
        from: "(304) 555-0122",
        name: "Sugar Grove Fire Department",
        account: "BR-10093",
        reason: "Business circuit down",
        outcome: "Escalated",
        seconds: 96,
        flagged: true,
        recordingUrl: null,
        transcript: [
          {
            who: "caller",
            text: "The business circuit is down at the fire station.",
          },
          {
            who: "assistant",
            text: "Please leave a voicemail. I will page the on-call technician with your message. This call will not be transferred.",
          },
        ],
      },
      {
        id: "call-4",
        time: "1:32 AM",
        from: "(304) 555-0199",
        name: "Oliver Brooks",
        account: "BR-12976",
        reason: "Planned maintenance",
        outcome: "Informed",
        seconds: 38,
        flagged: false,
        recordingUrl: null,
        transcript: [
          { who: "caller", text: "Is maintenance planned for my area?" },
          {
            who: "assistant",
            text: "There is no scheduled maintenance notice for your account.",
          },
        ],
      },
      {
        id: "call-5",
        time: "1:22 AM",
        from: "(681) 555-0166",
        name: "June Miller",
        account: "BR-11440",
        reason: "Service line damaged",
        outcome: "Escalated",
        seconds: 81,
        flagged: false,
        recordingUrl: null,
        transcript: [
          {
            who: "caller",
            text: "A tree pulled the service line down at Wolf Gap Road.",
          },
          {
            who: "assistant",
            text: "Please stay clear of damaged lines. Leave a message and I will page the on-call technician.",
          },
        ],
      },
      {
        id: "call-6",
        time: "12:48 AM",
        from: "(681) 555-0113",
        name: null,
        account: "Unknown",
        reason: "New service inquiry",
        outcome: "Message taken",
        seconds: 64,
        flagged: false,
        recordingUrl: null,
        transcript: [
          {
            who: "caller",
            text: "I would like to ask about fiber at my new address.",
          },
          { who: "assistant", text: "I can take your details for the office." },
        ],
      },
      {
        id: "call-7",
        time: "12:16 AM",
        from: "(304) 555-0157",
        name: "Mae Carter",
        account: "BR-10556",
        reason: "Payment portal",
        outcome: "Informed",
        seconds: 32,
        flagged: false,
        recordingUrl: null,
        transcript: [
          { who: "caller", text: "Where do I pay my bill?" },
          {
            who: "assistant",
            text: "You can use the customer portal listed on your statement.",
          },
        ],
      },
      {
        id: "call-8",
        time: "11:52 PM",
        from: "(304) 555-0188",
        name: null,
        account: "Unknown",
        reason: "Unknown",
        outcome: "Abandoned",
        seconds: 12,
        flagged: false,
        recordingUrl: null,
        transcript: [],
      },
    ],
    escalations: [
      {
        id: "esc-1",
        callId: "call-3",
        caller: "Sugar Grove Fire Department",
        summary:
          "Business circuit down at the fire station. Voicemail received.",
        state: "acked",
        target: "Dale Whitmore",
        time: "1:48 AM",
        severity: "Urgent",
        note: "",
      },
      {
        id: "esc-2",
        callId: "call-5",
        caller: "June Miller",
        summary: "Tree damaged the service drop at 1140 Wolf Gap Road.",
        state: "acked",
        target: "Dale Whitmore",
        time: "1:24 AM",
        severity: "Normal",
        note: "",
      },
    ],
    proposals: [
      {
        id: "prop-1",
        kind: "Create support ticket",
        caller: "Thomas Reed",
        rationale:
          "Repeated connection drops were reported. No known outage matches the account.",
        effect:
          "Create a ticket in the billing system with the caller’s message.",
        status: "pending",
        callId: "call-2",
      },
      {
        id: "prop-2",
        kind: "Send restoration text",
        caller: "Evelyn Harper",
        rationale: "The caller requested an update when service is restored.",
        effect: "Send a restoration text only after the outage is resolved.",
        status: "pending",
        callId: "call-1",
      },
      {
        id: "prop-3",
        kind: "Create support ticket",
        caller: "June Miller",
        rationale: "The service line needs a physical inspection.",
        effect: "Create a field-service ticket linked to the escalation.",
        status: "approved",
        callId: "call-5",
      },
    ],
    inbox: [
      {
        id: "in-1",
        kind: "Message",
        subject: "Connection drops after dinner",
        body: "Thomas reports repeated connection drops. Best callback time is after 8 AM.",
        name: "Thomas Reed",
        number: "(304) 555-0135",
        time: "1:57 AM",
        resolved: false,
      },
      {
        id: "in-2",
        kind: "Lead",
        subject: "Fiber availability on Mill Creek Road",
        body: "New resident would like pricing and installation availability. Address details are in the call record.",
        name: "New customer",
        number: "(681) 555-0113",
        time: "12:48 AM",
        resolved: false,
      },
      {
        id: "in-3",
        kind: "Ticket",
        subject: "Wolf Gap Road service drop",
        body: "Technician inspection requested after a tree damaged the service drop.",
        name: "June Miller",
        number: "(681) 555-0166",
        time: "1:22 AM",
        resolved: false,
      },
    ],
    flows: [
      {
        id: "after-hours",
        name: "After-hours answering",
        description:
          "Identify callers, check outages, take messages, and page on-call.",
        kind: "posture",
        icon: "moon",
        pinned: true,
        armed: true,
        armedUntil: null,
        stale: false,
        version: 3,
        steps: structuredClone(steps),
        edges: structuredClone(edges),
      },
      {
        id: "storm",
        name: "Storm response",
        description: "Shorter responses during widespread outages.",
        kind: "posture",
        icon: "storm",
        pinned: true,
        armed: false,
        armedUntil: null,
        stale: false,
        version: 2,
        steps: structuredClone(steps),
        edges: structuredClone(edges),
      },
      {
        id: "holiday",
        name: "Holiday greeting",
        description: "Use your saved office closure message.",
        kind: "posture",
        icon: "calendar",
        pinned: true,
        armed: false,
        armedUntil: null,
        stale: false,
        version: 1,
        steps: structuredClone(steps),
        edges: structuredClone(edges),
      },
      {
        id: "message",
        name: "Take a message",
        description: "Collect a voicemail for a matching call.",
        kind: "action",
        icon: "message",
        pinned: false,
        armed: false,
        armedUntil: null,
        stale: false,
        version: 1,
        steps: structuredClone(steps),
        edges: structuredClone(edges),
      },
    ],
    policy: {
      ticketWrite: "proposal",
      restoreSms: "proposal",
      officeHours: "Monday–Friday, 8 AM–5 PM",
      portal: "https://billing.blueridge.example",
      emergencyKeywords: [
        "fire station",
        "damaged service line",
        "business outage",
      ],
      ladder: [
        { target: "Dale Whitmore", channel: "SMS", afterMinutes: 0 },
        { target: "Secondary on-call", channel: "SMS", afterMinutes: 14 },
        { target: "Marcus Pettit", channel: "SMS", afterMinutes: 45 },
      ],
    },
    voice: {
      id: "rowan",
      stability: 2,
      likeness: 2,
      pace: 2,
      script:
        "Thank you for calling Blue Ridge Fiber. I am the automated after-hours assistant. How can I help you tonight?",
    },
    integrations: [
      {
        id: "sonar",
        name: "Sonar",
        kind: "Billing & accounts",
        status: "connected",
        detail: "Scoped read-only account lookup",
      },
      {
        id: "nms",
        name: "Network monitoring",
        kind: "Outage events",
        status: "unverified",
        detail: "No recent monitoring event",
      },
      {
        id: "cpe",
        name: "CPE monitoring",
        kind: "Customer equipment",
        status: "disconnected",
        detail: "Not connected",
      },
    ],
    users: [
      {
        id: "marcus",
        name: "Marcus Pettit",
        email: "marcus.pettit@blueridge.example",
        role: "owner",
        disabled: false,
      },
      {
        id: "dale",
        name: "Dale Whitmore",
        email: "dale@blueridge.example",
        role: "operator",
        disabled: false,
      },
      {
        id: "office",
        name: "Office team",
        email: "office@blueridge.example",
        role: "viewer",
        disabled: false,
      },
    ],
    brand: {
      name: "Blue Ridge Fiber",
      greeting: "Thank you for calling Blue Ridge Fiber.",
      emailDigest: true,
      smsAlerts: true,
    },
  };
}
